import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { obterIndicadores } from "../services/api";
import { teclaAtiva } from "../utils/teclaAtiva";

export default function IndicadoresPage() {
  const [ind, setInd] = useState(null);
  const [erro, setErro] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    obterIndicadores().then(setInd).catch((e) => setErro(e.message));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-text">Indicadores de reputação</h1>
        <p className="mt-2 text-gray-600">
          Rankings por <strong>taxa</strong> (não por volume bruto).
        </p>

        {erro && <p className="mt-4 text-red-600">{erro}</p>}
        {!ind && !erro && <p className="mt-4 text-gray-500">Carregando indicadores...</p>}

        {ind && (
          <>
            {/* Resumo geral */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <Kpi v={ind.geral.totalEmpresas} l="empresas avaliadas" />
              <Kpi v={ind.geral.totalDenuncias} l="relatos" />
              <Kpi v={`${Math.round(ind.geral.percResolvidas)}%`} l="índice de solução médio" />
              <Kpi v={`${Math.round(ind.geral.percSemResposta)}%`} l="sem resposta (média)" cor="text-red-600" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* A pergunta mais direta que fazemos ao candidato: você voltaria? É o ranking que mostra
                  quem trata bem, e não só quem responde rápido. */}
              <Ranking titulo="💚 Mais recomendadas (voltariam a se candidatar)" sufixo="%" itens={ind.maisRecomendadas} onSelect={navigate} cor="bg-emerald-500" unidade={["avaliação", "avaliações"]} />
              <Ranking titulo="🏆 Melhor índice de solução" sufixo="%" itens={ind.melhoresSolucao} onSelect={navigate} cor="bg-green-500" unidade={["reclamação", "reclamações"]} />
              <Ranking titulo="⭐ Melhor satisfação (nota média)" sufixo="" max={10} itens={ind.melhoresSatisfacao} onSelect={navigate} cor="bg-blue-500" unidade={["avaliação", "avaliações"]} />
              <Ranking titulo="⚡ Retorno mais rápido (dias)" sufixo=" dias" inverso itens={ind.maisRapidas} onSelect={navigate} cor="bg-indigo-500" />
              <Ranking titulo="🚩 Pior taxa de não resposta" sufixo="%" itens={ind.pioresSemResposta} onSelect={navigate} cor="bg-red-500" unidade={["reclamação", "reclamações"]} />
              <Ranking titulo="💙 Responde rápido no eaiRH (horas)" sufixo=" h" inverso itens={ind.respondeRapido} onSelect={navigate} cor="bg-sky-500" />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ v, l, cor = "text-gray-900" }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
      <div className={`text-2xl font-bold ${cor}`}>{v}</div>
      <div className="text-xs text-gray-500 mt-0.5">{l}</div>
    </div>
  );
}

// `unidade` = o que o "total" ao lado do nome está contando. Em ranking que vem de AVALIAÇÃO, contar
// "relatos" mentia sobre o tamanho da amostra (empresa com 2 relatos e 1 avaliação parecia ter 2 opiniões).
function Ranking({ titulo, itens = [], sufixo = "", max, inverso, cor, onSelect, unidade = ["relato", "relatos"] }) {
  if (!itens.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="font-bold text-gray-900">{titulo}</h3>
        <p className="mt-3 text-sm text-gray-400">Sem dados suficientes ainda.</p>
      </div>
    );
  }
  const maxVal = max ?? Math.max(...itens.map((i) => i.valor), 1);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="font-bold text-gray-900">{titulo}</h3>
      <ul className="mt-4 space-y-3">
        {itens.map((it, idx) => {
          const larg = inverso ? Math.max(8, 100 - (it.valor / maxVal) * 100) : (it.valor / maxVal) * 100;
          return (
            <li key={it.id} onClick={() => onSelect?.(`/empresas/${it.id}`)}
                {...teclaAtiva(() => onSelect?.(`/empresas/${it.id}`))}
                aria-label={`Ver reputação de ${it.nome}`}
                className="cursor-pointer group focus:outline-none focus:ring-2 focus:ring-primary rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-5 text-gray-500 font-semibold">{idx + 1}</span>
                <span className="font-medium text-gray-900 group-hover:text-primary">{it.nome}</span>
                {it.verificada && <span className="text-xs text-blue-600">✔</span>}
                {it.total != null && <span className="text-[11px] text-gray-400">· {it.total} {it.total === 1 ? unidade[0] : unidade[1]}</span>}
                <span className="ml-auto font-semibold text-gray-700">{it.valor}{sufixo}</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-gray-200">
                <div className={`h-2 rounded-full ${cor}`} style={{ width: `${larg}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
